import apiClient from '../api/config';
import { getCurrentLanguage } from '../config/languageConfig';

/**
 * 서비스 운영시간 체크 결과
 */
export interface OperationStatus {
  isOpen: boolean;
  message?: string;
  operationHours?: string; // 운영시간 정보 추가
  isClosingSoon?: boolean; // 30분 전 여부 추가
  minutesUntilClose?: number; // 마감까지 남은 시간(분) 추가
  endTime?: string; // 운영 종료 시간 (예: "16:29:00")
  isError?: boolean; // 에러 상태 추가
}

/**
 * live-on API 응답 인터페이스
 */
interface LiveOnApiResponse {
  start: string;
  end: string;
  isOpen: boolean; // 서버에서 판단한 운영 상태
  isClosingSoon: boolean;
  lang: string;
  messageEn: string;
  messageJa: string;
  messageZhCn: string;
  messageZhTw: string;
  serviceUserId?: string; // 서버에서 생성된 serviceUserId
}

/**
 * 로컬스토리지 키 상수
 */
const SERVICE_USER_ID_KEY = 'serviceUserId';

/**
 * 로컬스토리지에서 serviceUserId 조회
 */
const getServiceUserId = (): string | null => {
  try {
    return localStorage.getItem(SERVICE_USER_ID_KEY);
  } catch (error) {
    console.warn('[OperationTimeService] Failed to get serviceUserId from localStorage:', error);
    return null;
  }
};

/**
 * 로컬스토리지에 serviceUserId 저장
 */
const setServiceUserId = (serviceUserId: string): void => {
  try {
    localStorage.setItem(SERVICE_USER_ID_KEY, serviceUserId);
  } catch (error) {
    console.error('[OperationTimeService] Failed to save serviceUserId to localStorage:', error);
  }
};

/**
 * 언어별 메시지 추출
 */
const getLocalizedMessage = (response: LiveOnApiResponse, currentLang: string): string => {
  switch (currentLang.toLowerCase()) {
    case 'ko':
      // 한국어는 영어 메시지를 기본으로 사용 (서버에서 messageKo가 없으므로)
      return response.messageEn || `Operation time: ${response.start} - ${response.end} (KST)`;
    case 'ja':
      return response.messageJa || `営業時間: ${response.start} - ${response.end} (KST)`;
    case 'zh-cn':
      return response.messageZhCn || `营业时间: ${response.start} - ${response.end} (KST)`;
    case 'zh-tw':
      return response.messageZhTw || `營業時間: ${response.start} - ${response.end} (KST)`;
    case 'en':
    default:
      return response.messageEn || `Operation time: ${response.start} - ${response.end} (KST)`;
  }
};

/**
 * 서비스 운영시간 체크
 * @returns 운영 상태 정보
 */
export const checkOperationTime = async (): Promise<OperationStatus> => {
  try {
    const currentLang = getCurrentLanguage();
    const existingServiceUserId = getServiceUserId();
    
    // API 요청 URL 구성
    let apiUrl = `/api/v1/live-on?lang=${currentLang}`;
    if (existingServiceUserId) {
      apiUrl += `&serviceUserId=${existingServiceUserId}`;
    }
    
    const response = await apiClient.get(apiUrl, {
      timeout: 2000 // 2초 타임아웃
    });
    
    // 응답이 성공하면 isOpen 필드로 운영 상태 판단
    if (response.status === 200) {
      const responseData: LiveOnApiResponse = response.data;
      
      // 서버에서 serviceUserId가 새로 생성되어 전달된 경우 로컬스토리지에 저장
      if (responseData.serviceUserId && responseData.serviceUserId !== existingServiceUserId) {
        setServiceUserId(responseData.serviceUserId);
      }
      
      const localizedMessage = getLocalizedMessage(responseData, currentLang);
      
      // 서버의 isOpen 필드를 기준으로 운영 상태 반환
      return {
        isOpen: responseData.isOpen,
        message: responseData.isOpen ? 'Service is currently open' : 'Service is currently closed',
        operationHours: localizedMessage,
        isClosingSoon: responseData.isOpen ? responseData.isClosingSoon : false,
        minutesUntilClose: responseData.isOpen && responseData.isClosingSoon ? 30 : undefined,
        endTime: responseData.end
      };
    }
  } catch (error: any) {
    
    // 403 응답이나 다른 에러 응답에서도 isOpen 필드가 있으면 사용
    if (error.response?.data) {
      const responseData: LiveOnApiResponse = error.response.data;
      
      // serviceUserId가 전달된 경우 저장
      const existingServiceUserId = getServiceUserId();
      if (responseData.serviceUserId && responseData.serviceUserId !== existingServiceUserId) {
        setServiceUserId(responseData.serviceUserId);
      }
      
      // isOpen 필드가 존재하면 이를 기준으로 판단
      if (typeof responseData.isOpen === 'boolean') {
        const localizedMessage = getLocalizedMessage(responseData, getCurrentLanguage());
        
        return {
          isOpen: responseData.isOpen,
          message: responseData.isOpen ? 'Service is currently open' : 'Service is currently closed',
          operationHours: localizedMessage,
          isClosingSoon: responseData.isOpen ? responseData.isClosingSoon : false,
          minutesUntilClose: responseData.isOpen && responseData.isClosingSoon ? 30 : undefined,
          endTime: responseData.end
        };
      }
    }
    
    // 네트워크 에러, 타임아웃, 서버 에러(5xx) 등은 서버 다운으로 처리
    console.error('[OperationTimeService] Server down or network error:', error);
    return {
      isOpen: false,
      message: 'Service status unknown - server may be down',
      operationHours: 'Unable to check service status',
      isClosingSoon: false,
      minutesUntilClose: undefined,
      isError: true // 서버 다운 상태 표시
    };
  }
  
  // 기본 반환값 (도달하지 않을 코드)
  return {
    isOpen: false,
    message: 'Service status unknown',
    operationHours: 'Unable to check service status',
    isClosingSoon: false,
    minutesUntilClose: undefined,
    isError: true
  };
};

/**
 * 운영시간 체크 결과를 캐시하는 클래스
 */
class OperationTimeCache {
  private static instance: OperationTimeCache;
  private cachedStatus: OperationStatus | null = null;
  private lastCheckTime: number = 0;
  private readonly CACHE_DURATION = 30 * 1000; // 30초 캐시 (60초에서 감소)

  static getInstance(): OperationTimeCache {
    if (!OperationTimeCache.instance) {
      OperationTimeCache.instance = new OperationTimeCache();
    }
    return OperationTimeCache.instance;
  }

  async getOperationStatus(): Promise<OperationStatus> {
    const now = Date.now();
    
    // 캐시가 유효한 경우 캐시된 결과 반환
    if (this.cachedStatus && (now - this.lastCheckTime) < this.CACHE_DURATION) {
      return this.cachedStatus;
    }
    
    // 새로운 체크 수행
    this.cachedStatus = await checkOperationTime();
    this.lastCheckTime = now;
    
    return this.cachedStatus;
  }

  // 캐시 무효화 (강제 재체크용)
  invalidateCache(): void {
    this.cachedStatus = null;
    this.lastCheckTime = 0;
  }
}

// 캐시된 운영시간 체크 함수 (외부에서 사용)
export const getOperationStatus = (): Promise<OperationStatus> => {
  return OperationTimeCache.getInstance().getOperationStatus();
};

// 캐시 무효화 함수 (외부에서 사용)
export const invalidateOperationCache = (): void => {
  OperationTimeCache.getInstance().invalidateCache();
};

/**
 * serviceUserId 관리 유틸리티 함수들 (외부에서 사용 가능)
 */

/**
 * 현재 저장된 serviceUserId 조회
 * @returns serviceUserId 또는 null
 */
export const getCurrentServiceUserId = (): string | null => {
  return getServiceUserId();
};

/**
 * serviceUserId 수동 설정 (테스트 등의 목적으로 사용)
 * @param serviceUserId 설정할 serviceUserId
 */
export const setCurrentServiceUserId = (serviceUserId: string): void => {
  setServiceUserId(serviceUserId);
};

/**
 * serviceUserId 삭제 (로그아웃 등의 상황에서 사용)
 */
export const clearServiceUserId = (): void => {
  try {
    localStorage.removeItem(SERVICE_USER_ID_KEY);
  } catch (error) {
    console.error('[OperationTimeService] Failed to clear serviceUserId from localStorage:', error);
  }
}; 